import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  LinearProgress,
  Snackbar,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined';
import OilBarrelOutlinedIcon from '@mui/icons-material/OilBarrelOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import WaterDropOutlinedIcon from '@mui/icons-material/WaterDropOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOrganizationStore } from '../../stores/organization.store';
import { useDataEntryStore } from '../../stores/data-entry.store';
import { useUtilityProofStore } from '../../stores/utility-proof.store';
import { BranchSiteSelect, ReportingMonthPicker } from '../../components/data-entry/BranchMonthToolbar';
import BranchDetailsCard from '../../components/data-entry/BranchDetailsCard';
import UtilityMetricSection from '../../components/data-entry/UtilityMetricSection';
import UtilityProofsTab from '../../components/data-entry/UtilityProofsTab';
import UtilitySettingsPanel from '../../components/data-entry/UtilitySettingsPanel';
import { listUtilityCatalog } from '../../services/utility-emission.service';
import PageHeader from '../../components/PageHeader';
import { SetupCoachmark } from '../../components/selfSetup/SetupCoachmark';
import {
  HELP_GUIDE_DATA_SOURCE_SETTINGS,
  HELP_GUIDE_SAVE_UTILITY,
  HELP_GUIDE_UTILITY_ENTRY,
  HELP_GUIDE_UTILITY_PROOFS,
} from '../../constants/knowledgeBaseTryIt';
import { helpI18n } from '../../i18n/help';
import {
  clearHelpGuideActive,
  markHelpGuideActive,
} from '../../utils/helpGuideSession';
import { CollectionSiteSchema } from '../../types/collection-site.type';
import { PatchCollectionSiteStatisticsRequest } from '../../types/collection-site-statistics.type';
import {
  UtilityActivityType,
  UtilityCategory,
} from '../../types/utility-emission.type';

type DraftValue = { consumption: string; amount: string };
type Draft = Record<string, DraftValue>;

enum UtilityPageTab {
  ELECTRICITY = 'electricity',
  GAS = 'gas',
  OIL = 'oil',
  WATER = 'water',
  SETTINGS = 'settings',
}

function parseField(s: string): { value?: number; invalid: boolean } {
  const t = s.trim();
  if (t === '') return { invalid: false };
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return { invalid: true };
  return { value: n, invalid: false };
}

const categoryIcons: Record<UtilityCategory, React.ComponentType<SvgIconProps>> = {
  [UtilityCategory.WATER]: WaterDropOutlinedIcon,
  [UtilityCategory.ELECTRICITY]: BoltOutlinedIcon,
  [UtilityCategory.GAS]: LocalFireDepartmentOutlinedIcon,
  [UtilityCategory.OIL]: OilBarrelOutlinedIcon,
  [UtilityCategory.OTHER]: ScienceOutlinedIcon,
};

const utilityTabs: Array<{
  value: UtilityPageTab;
  category: UtilityCategory;
  label: string;
  Icon: React.ComponentType<SvgIconProps>;
}> = [
  {
    value: UtilityPageTab.ELECTRICITY,
    category: UtilityCategory.ELECTRICITY,
    label: 'Electricity',
    Icon: BoltOutlinedIcon,
  },
  {
    value: UtilityPageTab.GAS,
    category: UtilityCategory.GAS,
    label: 'Gas',
    Icon: LocalFireDepartmentOutlinedIcon,
  },
  {
    value: UtilityPageTab.OIL,
    category: UtilityCategory.OIL,
    label: 'Oil',
    Icon: OilBarrelOutlinedIcon,
  },
  {
    value: UtilityPageTab.WATER,
    category: UtilityCategory.WATER,
    label: 'Water',
    Icon: WaterDropOutlinedIcon,
  },
];

function draftFromActivities(catalog: UtilityActivityType[], activities: { utility_type_key: string; consumption: number; amount_sar: number }[]): Draft {
  const byKey = new Map(activities.map((activity) => [activity.utility_type_key, activity]));
  return Object.fromEntries(
    catalog.map((type) => {
      const activity = byKey.get(type.key);
      return [type.key, {
        consumption: activity ? String(activity.consumption) : '',
        amount: activity ? String(activity.amount_sar) : '',
      }];
    })
  );
}

const DataEntry: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { t: tHelp } = useTranslation('translation', { i18n: helpI18n });
  const navigate = useNavigate();
  const location = useLocation();
  const [helpGuide, setHelpGuide] = useState<string | null>(null);
  const { selectedOrganization } = useOrganizationStore();
  const {
    sites,
    selectedSiteId,
    selectedMonth,
    statistics,
    noRowYet,
    isLoadingSites,
    isLoadingStats,
    isSaving,
    error,
    clearError,
    loadPreferences,
    resetForOrganizationChange,
    fetchSites,
    setSelectedSiteId,
    setSelectedMonth,
    fetchStatistics,
    patchStatistics,
  } = useDataEntryStore();

  const resetUtilityProof = useUtilityProofStore((s) => s.reset);

  const [activeTab, setActiveTab] = useState<UtilityPageTab>(
    UtilityPageTab.ELECTRICITY
  );
  const [catalog, setCatalog] = useState<UtilityActivityType[]>([]);
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(true);
  const [draft, setDraft] = useState<Draft>({});
  const [localError, setLocalError] = useState<string | null>(null);
  const [dataTourPhase, setDataTourPhase] = useState<'form' | 'save'>('form');
  const [proofsTourPhase, setProofsTourPhase] = useState<'tab' | 'content'>('tab');
  const [settingsTourPhase, setSettingsTourPhase] = useState<'tab' | 'content'>('tab');

  const activeDataGuide = helpGuide === HELP_GUIDE_UTILITY_ENTRY;
  const showFormTip = activeDataGuide && dataTourPhase === 'form';
  const showSaveTip = activeDataGuide && dataTourPhase === 'save';
  const showProofsTabTip =
    helpGuide === HELP_GUIDE_UTILITY_PROOFS && proofsTourPhase === 'tab';
  const showProofsTip =
    helpGuide === HELP_GUIDE_UTILITY_PROOFS && proofsTourPhase === 'content';
  const showSettingsTabTip =
    helpGuide === HELP_GUIDE_DATA_SOURCE_SETTINGS && settingsTourPhase === 'tab';
  const showSettingsTip =
    helpGuide === HELP_GUIDE_DATA_SOURCE_SETTINGS && settingsTourPhase === 'content';

  const finishDataGuide = () => {
    setDataTourPhase('form');
    setProofsTourPhase('tab');
    setSettingsTourPhase('tab');
    if (helpGuide) {
      setHelpGuide(null);
      clearHelpGuideActive();
    }
  };

  /** Soft-dismiss (X) for Help guides only. */
  const softDismissDataGuide = () => {
    setDataTourPhase('form');
    setProofsTourPhase('tab');
    setSettingsTourPhase('tab');
    if (helpGuide) {
      setHelpGuide(null);
      clearHelpGuideActive();
    }
  };

  const advanceDataGuide = () => {
    if (showFormTip) {
      setDataTourPhase('save');
      return;
    }
    finishDataGuide();
  };

  const advanceProofsGuide = () => {
    if (showProofsTabTip) {
      setActiveTab(UtilityPageTab.ELECTRICITY);
      setProofsTourPhase('content');
      return;
    }
    finishDataGuide();
  };

  const advanceSettingsGuide = () => {
    if (showSettingsTabTip) {
      setActiveTab(UtilityPageTab.SETTINGS);
      setSettingsTourPhase('content');
      return;
    }
    finishDataGuide();
  };

  useEffect(() => {
    const guide = new URLSearchParams(location.search).get('helpGuide');
    if (
      guide !== HELP_GUIDE_UTILITY_ENTRY &&
      guide !== HELP_GUIDE_SAVE_UTILITY &&
      guide !== HELP_GUIDE_UTILITY_PROOFS &&
      guide !== HELP_GUIDE_DATA_SOURCE_SETTINGS
    ) {
      return;
    }
    markHelpGuideActive(guide === HELP_GUIDE_SAVE_UTILITY ? HELP_GUIDE_UTILITY_ENTRY : guide);
    if (guide === HELP_GUIDE_UTILITY_PROOFS) {
      setHelpGuide(HELP_GUIDE_UTILITY_PROOFS);
      setProofsTourPhase('tab');
      setActiveTab(UtilityPageTab.ELECTRICITY);
    } else if (guide === HELP_GUIDE_DATA_SOURCE_SETTINGS) {
      setHelpGuide(HELP_GUIDE_DATA_SOURCE_SETTINGS);
      setSettingsTourPhase('tab');
      setActiveTab(UtilityPageTab.ELECTRICITY);
    } else {
      setHelpGuide(HELP_GUIDE_UTILITY_ENTRY);
      setDataTourPhase(guide === HELP_GUIDE_SAVE_UTILITY ? 'save' : 'form');
      setActiveTab(UtilityPageTab.ELECTRICITY);
    }
    navigate({ pathname: location.pathname, search: '' }, { replace: true });
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    if (helpGuide !== HELP_GUIDE_UTILITY_ENTRY) {
      setDataTourPhase('form');
    }
  }, [helpGuide]);

  useEffect(() => {
    if (showProofsTabTip) {
      window.requestAnimationFrame(() => {
        document
          .getElementById('help-utility-proofs-tab')
          ?.scrollIntoView({ behavior: 'auto', block: 'center' });
      });
      return;
    }
    if (showProofsTip) {
      window.requestAnimationFrame(() => {
        document
          .getElementById('help-utility-proofs')
          ?.scrollIntoView({ behavior: 'auto', block: 'center' });
      });
      return;
    }
    if (showSettingsTabTip) {
      window.requestAnimationFrame(() => {
        document
          .getElementById('help-utility-settings-tab')
          ?.scrollIntoView({ behavior: 'auto', block: 'center' });
      });
      return;
    }
    if (showSettingsTip) {
      window.requestAnimationFrame(() => {
        document
          .getElementById('help-data-source-settings')
          ?.scrollIntoView({ behavior: 'auto', block: 'center' });
      });
      return;
    }
    if (!activeDataGuide) return;
    const id = showSaveTip
      ? 'help-utility-entry-save'
      : 'help-utility-entry-form';
    window.requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'auto', block: 'center' });
    });
  }, [
    activeDataGuide,
    showSaveTip,
    showFormTip,
    showProofsTabTip,
    showProofsTip,
    showSettingsTabTip,
    showSettingsTip,
  ]);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  useEffect(() => {
    let active = true;
    setIsLoadingCatalog(true);
    listUtilityCatalog()
      .then((rows) => {
        if (active) setCatalog(rows);
      })
      .catch(() => {
        if (active) setLocalError('Unable to load the utility catalog.');
      })
      .finally(() => {
        if (active) setIsLoadingCatalog(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedOrganization?.id) return;
    resetForOrganizationChange();
    resetUtilityProof();
    void fetchSites(selectedOrganization.id);
  }, [selectedOrganization?.id, resetForOrganizationChange, resetUtilityProof, fetchSites]);

  useEffect(() => {
    if (!selectedOrganization?.id || !selectedSiteId) return;
    setDraft({});
    void fetchStatistics(selectedOrganization.id, selectedSiteId, selectedMonth);
  }, [selectedOrganization?.id, selectedSiteId, selectedMonth, fetchStatistics]);

  useEffect(() => {
    if (!isLoadingStats && statistics && !isLoadingCatalog) {
      setDraft(draftFromActivities(catalog, statistics.utility_activities ?? []));
    }
  }, [isLoadingStats, isLoadingCatalog, statistics, catalog]);

  const selectedSite: CollectionSiteSchema | null = useMemo(
    () => sites.find((s) => s.id === selectedSiteId) ?? null,
    [sites, selectedSiteId]
  );

  const showBlockingLoader = isLoadingSites && sites.length === 0;
  const activeUtilityTab =
    utilityTabs.find((tab) => tab.value === activeTab) ?? null;
  const activeUtilityTypes = useMemo(
    () =>
      activeUtilityTab
        ? catalog.filter((utilityType) => utilityType.category === activeUtilityTab.category)
        : [],
    [activeUtilityTab, catalog]
  );

  const updateDraft = useCallback((key: string, field: keyof DraftValue, value: string) => {
    setDraft((current) => ({
      ...current,
      [key]: { ...(current[key] ?? { consumption: '', amount: '' }), [field]: value },
    }));
  }, []);

  const buildFullDraftPatch = useCallback((): PatchCollectionSiteStatisticsRequest | null => {
    const activities = [];
    for (const utilityType of catalog) {
      const value = draft[utilityType.key] ?? { consumption: '', amount: '' };
      const c = parseField(value.consumption);
      const a = parseField(value.amount);
      if (c.invalid || a.invalid) {
        setLocalError('Enter valid non-negative numbers, or leave fields empty to skip.');
        return null;
      }
      if (c.value !== undefined || a.value !== undefined) {
        activities.push({
          utility_type_key: utilityType.key,
          consumption: c.value ?? 0,
          amount_sar: a.value ?? 0,
        });
      }
    }
    setLocalError(null);
    return { utility_activities: activities };
  }, [catalog, draft]);

  const handleSaveAll = useCallback(async () => {
    if (!selectedOrganization?.id || !selectedSiteId) return;
    const patch = buildFullDraftPatch();
    if (!patch) return;
    const ok = await patchStatistics(
      selectedOrganization.id,
      selectedSiteId,
      selectedMonth,
      patch
    );
    if (ok) setLocalError(null);
  }, [selectedOrganization?.id, selectedSiteId, selectedMonth, buildFullDraftPatch, patchStatistics]);

  const snackbarOpen = Boolean(error || localError);
  const snackbarMessage = error || localError || '';

  const handleSnackbarClose = () => {
    clearError();
    setLocalError(null);
  };

  if (!selectedOrganization) {
    return (
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          {t('dataEntry.title')}
        </Typography>
        <Alert severity="info">{t('dataEntry.selectOrganization')}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {isLoadingSites && <LinearProgress sx={{ mb: 2 }} />}

      {/* Header: title + branch selector + month picker + site details */}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <PageHeader
          icon={<EditNoteOutlinedIcon />}
          title={t('dataEntry.title')}
          actions={
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 2,
                rowGap: 1.5,
                maxWidth: '100%',
                justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              }}
            >
              <Box
                sx={{
                  flex: '1 1 200px',
                  minWidth: 160,
                  maxWidth: { lg: 360 },
                  width: { xs: '100%', sm: 'auto' },
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <BranchSiteSelect
                  sites={sites}
                  selectedSiteId={selectedSiteId}
                  onSiteChange={setSelectedSiteId}
                  disabled={showBlockingLoader || isSaving}
                />
              </Box>
              <Box
                sx={{
                  flex: '0 1 200px',
                  minWidth: 168,
                  maxWidth: 240,
                  width: { xs: '100%', sm: 'auto' },
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <ReportingMonthPicker
                  selectedMonth={selectedMonth}
                  onMonthChange={setSelectedMonth}
                  disabled={showBlockingLoader || isSaving}
                />
              </Box>
              <Box
                sx={{
                  flex: '5 1 416px',
                  minWidth: { xs: '100%', sm: 360 },
                  flexGrow: 1,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <BranchDetailsCard site={selectedSite} />
              </Box>
            </Box>
          }
        />
      </LocalizationProvider>

      {sites.length === 0 && !isLoadingSites && (
        <Alert severity="warning" icon={<StorefrontOutlinedIcon fontSize="inherit" />} sx={{ mb: 2 }}>
          {t('common.noBranchesForOrg')}
        </Alert>
      )}

      {/* Tabs */}
      <Box
        sx={{
          bgcolor: theme.palette.background.paper,
          borderRadius: '8px',
          boxShadow: theme.shadows[2],
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, value: UtilityPageTab) => {
            setActiveTab(value);
            if (showProofsTabTip && value !== UtilityPageTab.SETTINGS) {
              setProofsTourPhase('content');
            }
            if (showSettingsTabTip && value === UtilityPageTab.SETTINGS) {
              setSettingsTourPhase('content');
            }
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 2,
            pt: 1,
            '& .MuiTab-root': {
              minHeight: 52,
              textTransform: 'none',
              fontWeight: 600,
            },
          }}
        >
          {utilityTabs.map(({ value, label }, index) => (
            <Tab
              key={value}
              value={value}
              label={
                index === 0 && showProofsTabTip ? (
                  <SetupCoachmark
                    open
                    title={tHelp('knowledgeBase.utilityProofsTabTitle')}
                    subtitle={tHelp('knowledgeBase.utilityProofsTabSubtitle')}
                    onDismiss={softDismissDataGuide}
                    onNext={advanceProofsGuide}
                    nextLabel={t('selfSetup.tourNext')}
                  >
                    <Box
                      id="help-utility-proofs-tab"
                      component="span"
                      sx={{ display: 'inline-flex', alignItems: 'center' }}
                    >
                      {label}
                    </Box>
                  </SetupCoachmark>
                ) : (
                  label
                )
              }
            />
          ))}
          <Tab
            value={UtilityPageTab.SETTINGS}
            id="help-utility-settings-tab"
            label={
              <SetupCoachmark
                open={showSettingsTabTip}
                title={tHelp('knowledgeBase.dataSourceSettingsTabTitle')}
                subtitle={tHelp('knowledgeBase.dataSourceSettingsTabSubtitle')}
                onDismiss={softDismissDataGuide}
                onNext={advanceSettingsGuide}
                nextLabel={t('selfSetup.tourNext')}
              >
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                  {t('dataEntry.tabs.settings')}
                </Box>
              </SetupCoachmark>
            }
          />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {activeUtilityTab && (
            <>
              {(isLoadingStats || isLoadingCatalog) && <LinearProgress sx={{ mb: 2 }} />}

              <SetupCoachmark
                open={showFormTip}
                title={tHelp('knowledgeBase.utilityEntryGuideTitle')}
                subtitle={tHelp('knowledgeBase.utilityEntryGuideSubtitle')}
                onDismiss={softDismissDataGuide}
                onNext={advanceDataGuide}
                nextLabel={t('selfSetup.tourNext')}
                fullWidth
                placement="top"
              >
                <Box id="help-utility-entry-form">
                  {noRowYet && !isLoadingStats && statistics && (
                    <Alert
                      severity="info"
                      icon={<CalendarMonthOutlinedIcon fontSize="inherit" />}
                      sx={{ mb: 2 }}
                    >
                      {t('dataEntry.noRowYet')}
                    </Alert>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <activeUtilityTab.Icon color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {activeUtilityTab.label} data
                    </Typography>
                  </Box>

                  {!isLoadingCatalog && activeUtilityTypes.length === 0 && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      No active {activeUtilityTab.label.toLowerCase()} utility types are configured.
                    </Alert>
                  )}

                  <Grid container spacing={3}>
                    {activeUtilityTypes.map((utilityType) => {
                      const value = draft[utilityType.key] ?? { consumption: '', amount: '' };
                      const Icon = categoryIcons[utilityType.category];
                      return (
                        <Grid item xs={12} md={6} key={utilityType.key}>
                          <UtilityMetricSection
                            title={utilityType.label}
                            description={
                              utilityType.ghg_scope
                                ? `Scope ${utilityType.ghg_scope} activity`
                                : 'Utility activity and billing'
                            }
                            Icon={Icon}
                            consumptionLabel={`Consumption (${utilityType.canonical_unit})`}
                            amountLabel={t('dataEntry.utilities.amountSar')}
                            consumptionValue={value.consumption}
                            amountValue={value.amount}
                            onConsumptionChange={(value) =>
                              updateDraft(utilityType.key, 'consumption', value)
                            }
                            onAmountChange={(value) =>
                              updateDraft(utilityType.key, 'amount', value)
                            }
                            disabled={
                              !selectedSiteId ||
                              isLoadingStats ||
                              isLoadingCatalog ||
                              isSaving ||
                              !statistics
                            }
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              </SetupCoachmark>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <SetupCoachmark
                  open={showSaveTip}
                  title={tHelp('knowledgeBase.saveUtilityGuideTitle')}
                  subtitle={tHelp('knowledgeBase.saveUtilityGuideSubtitle')}
                  onDismiss={softDismissDataGuide}
                  onNext={finishDataGuide}
                  nextLabel={t('selfSetup.tourDone')}
                >
                  <Button
                    id="help-utility-entry-save"
                    variant="contained"
                    size="large"
                    startIcon={<SaveOutlinedIcon />}
                    onClick={() => void handleSaveAll()}
                    disabled={
                      activeUtilityTypes.length === 0 ||
                      !selectedSiteId ||
                      isLoadingStats ||
                      isLoadingCatalog ||
                      isSaving ||
                      !statistics
                    }
                  >
                    {isSaving ? 'Saving…' : `Save ${activeUtilityTab.label}`}
                  </Button>
                </SetupCoachmark>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Proof documents
              </Typography>
              <SetupCoachmark
                open={showProofsTip}
                title={tHelp('knowledgeBase.utilityProofsGuideTitle')}
                subtitle={tHelp('knowledgeBase.utilityProofsGuideSubtitle')}
                onDismiss={softDismissDataGuide}
                onNext={finishDataGuide}
                nextLabel={t('selfSetup.tourDone')}
                fullWidth
              >
                <Box id="help-utility-proofs">
                  <UtilityProofsTab
                    siteId={selectedSiteId}
                    selectedMonth={selectedMonth}
                    utilityTypes={activeUtilityTypes}
                  />
                </Box>
              </SetupCoachmark>
            </>
          )}

          {activeTab === UtilityPageTab.SETTINGS && (
            <SetupCoachmark
              open={showSettingsTip}
              title={tHelp('knowledgeBase.dataSourceSettingsGuideTitle')}
              subtitle={tHelp('knowledgeBase.dataSourceSettingsGuideSubtitle')}
              onDismiss={softDismissDataGuide}
              onNext={finishDataGuide}
              nextLabel={t('selfSetup.tourDone')}
              fullWidth
              placement="top"
            >
              <Box id="help-data-source-settings">
                <UtilitySettingsPanel siteId={selectedSiteId} />
              </Box>
            </SetupCoachmark>
          )}
        </Box>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="error" variant="filled" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DataEntry;
